/**
 * Load More functionality for catalog pages
 * Handles dynamic loading of additional products without full page refresh
 */

export function initLoadMore() {
    const btn = document.getElementById('loadMoreBtn') as HTMLButtonElement | null;
    if (!btn) return;

    btn.addEventListener('click', handleLoadMore);
}

async function handleLoadMore(e: Event) {
    const btn = e.currentTarget as HTMLButtonElement;
    const page = parseInt(btn.dataset.page || '1');
    const totalPages = parseInt(btn.dataset.totalPages || '1');
    const category = btn.dataset.category || '';
    const facets = btn.dataset.facets || '';
    const sort = btn.dataset.sort || 'asc';

    // Determine the type based on current URL
    const type = getPageType();
    const query = getSearchQuery();

    // Show loading state
    btn.disabled = true;
    const originalText = btn.textContent;
    btn.innerHTML = '<span class="spinner-border spinner-border-sm me-2"></span>Loading...';

    try {
        const params = new URLSearchParams({
            category,
            page: page.toString(),
            facets,
            sort,
            type
        });

        if (query) {
            params.set('query', query);
        }

        const response = await fetch(`/catalog/load-more?${params}`);

        if (!response.ok) {
            throw new Error('Failed to load products');
        }

        const html = await response.text();
        const productGrid = document.getElementById('productGrid');

        if (productGrid) {
            productGrid.insertAdjacentHTML('beforeend', html);
        }

        // Update button state for next page
        if (page >= totalPages) {
            // No more pages - remove the button
            btn.parentElement?.remove();
        } else {
            // Update for next page
            btn.dataset.page = (page + 1).toString();
            btn.disabled = false;
            btn.textContent = originalText;
        }

    } catch (error) {
        console.error('Load more failed:', error);
        btn.disabled = false;
        btn.textContent = 'Load More Products';
    }
}

function getPageType(): string {
    const path = window.location.pathname;

    if (path.startsWith('/collections/')) {
        return 'collection';
    }
    if (path.startsWith('/search')) {
        return 'search';
    }
    return 'category';
}

function getSearchQuery(): string | null {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('query');
}

/**
 * Reset the load more button when filters change
 * Call this from filters.ts after updating the product grid
 */
export function resetLoadMore() {
    const btn = document.getElementById('loadMoreBtn') as HTMLButtonElement | null;
    if (btn) {
        btn.dataset.page = '2'; // Reset to page 2 since page 1 is just loaded
    }
}

/**
 * Update the load more button with new data after filter change
 */
export function updateLoadMoreButton(newPage: number, totalPages: number, facets: string, sort: string) {
    const container = document.getElementById('loadMoreContainer');
    const btn = document.getElementById('loadMoreBtn') as HTMLButtonElement | null;

    if (totalPages <= 1) {
        // No pagination needed - hide/remove button
        container?.remove();
        return;
    }

    if (!btn && container === null) {
        // Need to create the button
        const productGrid = document.getElementById('productGrid');
        if (productGrid) {
            const newContainer = document.createElement('div');
            newContainer.id = 'loadMoreContainer';
            newContainer.className = 'text-center my-4';
            newContainer.innerHTML = `
                <button type="button" id="loadMoreBtn" class="btn btn-lg btn-outline-dark"
                        data-page="2"
                        data-total-pages="${totalPages}"
                        data-facets="${facets}"
                        data-sort="${sort}">
                    Load More Products
                </button>
            `;
            productGrid.parentElement?.appendChild(newContainer);

            // Re-attach event listener
            const newBtn = document.getElementById('loadMoreBtn');
            newBtn?.addEventListener('click', handleLoadMore);
        }
    } else if (btn) {
        // Update existing button
        btn.dataset.page = (newPage + 1).toString();
        btn.dataset.totalPages = totalPages.toString();
        btn.dataset.facets = facets;
        btn.dataset.sort = sort;
        btn.disabled = false;
        btn.textContent = 'Load More Products';
    }
}
